/**
 * CASH CASINO — the settings menu behind the top bar's cog.
 *
 * This is roulette's RouletteMenu, ported: same scrim, same panel hanging under the bar's
 * top-right corner, same tile grammar (icon over label over state), same close cross sitting on the
 * panel's corner. Copied rather than imported because roulette's version is styled entirely from
 * `rlt-*` rules inside roulette.css — reaching for it would mean pulling that whole stylesheet into
 * this game to get one panel. The classes are `cs-*` here and the palette is this table's gold.
 *
 * Voice and My bets are not here: this game has no announcer, and its history has its own door in
 * the foot. The tiles are the ones a slot player actually has an opinion about.
 */
import { press } from './press';

function Tile({ label, wide, accent, on, state, icon, onTap }: {
  label: string;
  wide?: boolean;
  accent?: boolean;
  /** for the toggles — drives the lit state and, by default, the On/Off line */
  on?: boolean;
  /** overrides the On/Off line */
  state?: string;
  icon: React.ReactNode;
  onTap: () => void;
}) {
  return (
    <button type="button"
            className={`cs-tile ${wide ? 'cs-tile--wide' : ''} ${accent ? 'cs-tile--accent' : ''} ${on === false ? 'cs-tile--off' : ''}`}
            {...press(onTap)}>
      <span className="cs-tile-icon">{icon}</span>
      <span className="cs-tile-label">{label}</span>
      {(state ?? on) !== undefined && (
        <span className="cs-tile-state">{state ?? (on ? 'On' : 'Off')}</span>
      )}
    </button>
  );
}

/* One 24-unit box for every icon, so they carry the same optical weight. */
const I = {
  exit: (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4v-2H6V6h4z" />
      <path d="M14.6 7.4 13.2 8.8 15.4 11H9v2h6.4l-2.2 2.2 1.4 1.4L19.4 12z" />
    </svg>
  ),
  rules: (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm1 15h-2v-2h2zm1.8-6.3-.9.9c-.6.6-.9 1.1-.9 2.4h-2v-.5c0-1 .4-1.9 1.1-2.5l1.2-1.2c.4-.4.6-.9.5-1.5-.1-.7-.7-1.2-1.4-1.3-.9-.1-1.7.5-1.9 1.3H8.6C8.8 6.5 10.2 5.2 12 5.2c1.9 0 3.5 1.4 3.6 3.2.1.9-.3 1.7-.8 2.3z" />
    </svg>
  ),
  sound: (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path d="M4 9v6h4l5 5V4L8 9H4z" />
      <path d="M16.5 12a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4z" />
      <path d="M14 3.2v2.1a6.8 6.8 0 0 1 0 13.4v2.1a8.9 8.9 0 0 0 0-17.6z" />
    </svg>
  ),
  music: (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path d="M9 17.2V6.3l10-2v10.9h-2V6.7l-6 1.2v9.3z" />
      <ellipse cx="7" cy="17.6" rx="2.9" ry="2.4" />
      <ellipse cx="17" cy="15.6" rx="2.9" ry="2.4" />
    </svg>
  ),
  history: (
    <svg viewBox="0 0 24 24" aria-hidden>
      <rect x="3" y="4" width="18" height="3.2" rx="1.4" />
      <rect x="3" y="10.4" width="18" height="3.2" rx="1.4" />
      <rect x="3" y="16.8" width="12" height="3.2" rx="1.4" />
    </svg>
  ),
};

export function CasinoMenu({
  open, onClose, soundOn, musicOn, onToggleSound, onToggleMusic, onRules, onHistory, onExitGame,
}: {
  open: boolean;
  onClose: () => void;
  soundOn: boolean;
  musicOn: boolean;
  onToggleSound: () => void;
  onToggleMusic: () => void;
  onRules: () => void;
  onHistory: () => void;
  onExitGame: () => void;
}) {
  if (!open) return null;
  return (
    <div className="cs-menu-scrim" {...press(onClose)}>
      {/* stopPropagation with a plain handler — press() is for taps that DO something, and
          swallowing the scrim's tap is the opposite of that */}
      <div className="cs-menu" onPointerDown={(e) => e.stopPropagation()}>
        <button type="button" className="cs-menu-x" {...press(onClose)} aria-label="Close">
          <svg viewBox="0 0 24 24" aria-hidden>
            <path d="M18.3 5.7 12 12l6.3 6.3-1.4 1.4L10.6 13.4 4.3 19.7 2.9 18.3 9.2 12 2.9 5.7l1.4-1.4 6.3 6.3 6.3-6.3z" />
          </svg>
        </button>

        {/* the two audio switches stay SEPARATE: wanting the reels without the bed, or the bed
            without the reels, are both real preferences */}
        <div className="cs-menu-row">
          <Tile label="Sounds" icon={I.sound} on={soundOn} onTap={onToggleSound} />
          <Tile label="Music" icon={I.music} on={musicOn} onTap={onToggleMusic} />
        </div>

        <Tile wide label="Rules" icon={I.rules} onTap={onRules} />
        <Tile wide label="My history" icon={I.history} onTap={onHistory} />

        {/* the door out gets its own line and the only warm tile in here, because it is the one
            thing that ends the session and should never be hit by accident */}
        <Tile wide accent label="Exit game" icon={I.exit} onTap={onExitGame} />
      </div>
    </div>
  );
}
